import { useCallback, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  CheckIcon,
  ProhibitIcon,
  ShieldCheckIcon,
} from "@phosphor-icons/react";
import { discardLead, loadHeldLeads, releaseLead, siteName } from "../lib/data.ts";
import { useAsync, useDebounced } from "../lib/useAsync.ts";
import { relativeTime } from "../lib/format.ts";
import {
  Button,
  Chevron,
  EmptyState,
  ErrorState,
  Panel,
  RowSkeleton,
  SkeletonReveal,
} from "../components/ui.tsx";
import { LeadDetail } from "../components/LeadDetail.tsx";
import { useLiveRefresh } from "../lib/live.ts";
import type { LeadRecord } from "@pipeline/types.ts";

export function Held({ notify }: { notify: (tone: "ok" | "bad", text: string) => void }) {
  const state = useAsync<LeadRecord[]>(() => loadHeldLeads(), []);
  const refresh = useDebounced(state.reload, 800);
  useLiveRefresh(refresh);

  // Spam-or-not is decided on the full submission, so each row opens into the
  // same detail panel as the Leads screen — message, qualifying answers, the
  // lot. Collapsed rows would leave the operator judging on a name alone.
  const [openId, setOpenId] = useState<string | null>(null);

  const act = useCallback(
    async (lead: LeadRecord, kind: "release" | "discard") => {
      // Optimistic: the row leaves immediately. Reviewing a queue should feel
      // like clearing a queue, not like waiting for a server.
      state.patch((leads) => leads.filter((item) => item.id !== lead.id));

      try {
        if (kind === "release") {
          await releaseLead(lead.id);
          notify("ok", `${lead.name || "Lead"} released. It will forward within seconds.`);
        } else {
          await discardLead(lead.id);
          notify("ok", `${lead.name || "Lead"} marked as spam.`);
        }
      } catch {
        state.reload();
        notify("bad", "That did not go through. Nothing was changed.");
      }
    },
    [state, notify],
  );

  if (state.error && !state.data) {
    return <ErrorState message={state.error} onRetry={state.reload} />;
  }

  const leads = state.data ?? [];

  return (
    <div className="stack stack--5">
      <header className="page-head">
        <div>
          <h1 className="page-head__title">Held for review</h1>
          <p className="page-head__sub">
            Leads the quality checks were not confident about. They have not been sent to any
            partner.
          </p>
        </div>
      </header>

      <Panel flush>
        <SkeletonReveal loading={state.loading} skeleton={<RowSkeleton rows={3} />}>
          {leads.length === 0 ? (
            <EmptyState
              icon={<ShieldCheckIcon size={22} />}
              title="Nothing held"
              body="Every lead that arrived was confident enough to forward automatically. Anything the checks flag will wait here for you."
            />
          ) : (
            <div className="records">
              <AnimatePresence initial={false}>
            {leads.map((lead) => {
              const open = openId === lead.id;
              return (
              <motion.article
                className="lead-row t-acc"
                data-open={open}
                key={lead.id}
                layout
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: "hidden" }}
                transition={{ type: "spring", duration: 0.3, bounce: 0 }}
              >
                {/* The disclosure control is the chevron button; the rest of
                    the row toggles too as a bigger target, but steps aside for
                    the real links and the Spam/Release buttons inside it. */}
                <div
                  className="record record--held"
                  onClick={(event) => {
                    if ((event.target as HTMLElement).closest("a, button")) return;
                    setOpenId(open ? null : lead.id);
                  }}
                >
                  <div className="record__primary">
                    <p className="record__name">{lead.name || "Unnamed enquiry"}</p>
                    <p className="record__meta">
                      {[lead.company, siteName(lead.siteKey)].filter(Boolean).join(" · ")}
                    </p>

                    {/* Why it was held. Without this the operator is guessing. */}
                    {lead.qualityReasons.length > 0 && (
                      <div className="tags">
                        {lead.qualityReasons.slice(0, 3).map((reason) => (
                          <span className="tag" key={reason}>
                            <span className="tag__value">{reason}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="record__contact">
                    {lead.email && <a href={`mailto:${lead.email}`}>{lead.email}</a>}
                    {lead.phone && <a href={`tel:${lead.phone}`}>{lead.phone}</a>}
                    <span className="dim mono" style={{ fontSize: "var(--t-xs)" }}>
                      {relativeTime(lead.submittedAt)}
                    </span>
                  </div>

                  <div className="record__actions">
                    <Button
                      variant="quiet"
                      size="sm"
                      icon={<ProhibitIcon size={13} weight="bold" />}
                      onClick={() => void act(lead, "discard")}
                      aria-label={`Mark ${lead.name || "lead"} as spam`}
                    >
                      Spam
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      icon={<CheckIcon size={13} weight="bold" />}
                      onClick={() => void act(lead, "release")}
                      aria-label={`Release ${lead.name || "lead"} to the partner`}
                    >
                      Release
                    </Button>
                  </div>

                  <button
                    type="button"
                    className="record__chevron t-acc-chevron"
                    aria-expanded={open}
                    aria-label={`${open ? "Hide" : "Show"} details for ${lead.name || "this lead"}`}
                    onClick={() => setOpenId(open ? null : lead.id)}
                  >
                    <Chevron size={14} />
                  </button>
                </div>

                <div className="t-acc-panel">
                  <div className="t-acc-panel-inner">
                    <LeadDetail entry={{ lead, deliveries: [] }} />
                  </div>
                </div>
              </motion.article>
              );
            })}
              </AnimatePresence>
            </div>
          )}
        </SkeletonReveal>
      </Panel>

      {leads.length > 0 && (
        <p className="muted" style={{ fontSize: "var(--t-xs)" }}>
          Releasing marks the lead clean and queues it. The partner receives it within seconds.
        </p>
      )}
    </div>
  );
}
